import { fetchAuthSession } from 'aws-amplify/auth';

export type CopilotWsMessage = {
  type: string;
  [key: string]: unknown;
};

type MessageHandler = (data: CopilotWsMessage) => void;

interface ConnectOptions {
  conversationId?: string;
  caseId?: string;
}

type WsAuth =
  | { kind: 'cognito'; token: string }
  | { kind: 'components'; value: string };

async function getWsAuth(): Promise<WsAuth> {
  const stored = window.sessionStorage.getItem('gekko/authheader');
  if (stored) {
    if (stored.startsWith('GekkoComponents ')) {
      return {
        kind: 'components',
        value: stored.slice('GekkoComponents '.length),
      };
    }
    return {
      kind: 'cognito',
      token: stored.startsWith('Bearer ') ? stored.slice(7) : stored,
    };
  }

  const embedded = window.sessionStorage.getItem('gekko/embeddedTokens');
  if (embedded) {
    try {
      const parsed = JSON.parse(embedded);
      if (parsed.accessToken) {
        return { kind: 'cognito', token: parsed.accessToken as string };
      }
    } catch {
      // fall through
    }
  }

  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString();
    if (token) return { kind: 'cognito', token };
  } catch {
    // fall through
  }

  throw new Error('No auth token available for copilot WS');
}

export class CopilotWsClient {
  private ws: WebSocket | null = null;
  private endpoint: string;
  private handlers = new Set<MessageHandler>();
  private statusHandlers = new Set<(connected: boolean) => void>();
  private currentConversationId?: string;
  private pingInterval: ReturnType<typeof setInterval> | null = null;

  constructor(endpoint: string) {
    this.endpoint = endpoint;
  }

  onMessage(handler: MessageHandler): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  onStatusChange(handler: (connected: boolean) => void): () => void {
    this.statusHandlers.add(handler);
    return () => this.statusHandlers.delete(handler);
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  async connect({
    conversationId,
    caseId,
  }: ConnectOptions = {}): Promise<void> {
    if (this.ws && this.ws.readyState <= 1) {
      this.ws.close(1000, 'reconnecting');
    }
    const auth = await getWsAuth();
    const params = new URLSearchParams();
    if (auth.kind === 'cognito') {
      params.append('token', auth.token);
    } else {
      params.append('componentsAuth', auth.value);
    }
    if (conversationId) params.append('conversationId', conversationId);
    if (caseId) params.append('caseId', caseId);

    const url = `${this.endpoint}?${params.toString()}`;
    const ws = new WebSocket(url);
    this.ws = ws;

    ws.onopen = () => {
      this.statusHandlers.forEach((h) => h(true));
      this.pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ action: 'ping' }));
        }
      }, 30000);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as CopilotWsMessage;
        this.handlers.forEach((h) => h(data));
      } catch (err) {
        console.error('Failed to parse copilot WS message', err);
      }
    };

    ws.onclose = () => {
      if (this.pingInterval) {
        clearInterval(this.pingInterval);
        this.pingInterval = null;
      }
      this.statusHandlers.forEach((h) => h(false));
    };

    ws.onerror = (err) => {
      console.error('Copilot WS error', err);
    };

    this.currentConversationId = conversationId;
  }

  sendMessage(
    message: string,
    conversationId: string,
    attachmentIds: string[] = []
  ): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('Copilot WS not connected');
    }
    this.ws.send(
      JSON.stringify({
        action: 'sendMessage',
        message,
        conversationId,
        attachmentIds,
        timestamp: new Date().toISOString(),
      })
    );
    this.currentConversationId = conversationId;
  }

  getCurrentConversationId(): string | undefined {
    return this.currentConversationId;
  }

  disconnect(): void {
    if (this.ws) this.ws.close(1000, 'user disconnect');
    if (this.pingInterval) clearInterval(this.pingInterval);
    this.pingInterval = null;
    this.ws = null;
  }
}
